// Finding-waivers API client. Mirrors the conventions of
// lib/dataDictionaryFindingsApi.ts so the surface stays consistent
// across the resolver-related panels. One shared endpoint backs the
// "Waive" button on all three Phase-4 findings panels.

import type {
  FindingWaiverRequest,
  FindingWaiverResponse,
} from '@/types/findingWaivers'

const API_BASE = '/api'

function getOrCreateClientId(): string {
  const KEY = 'ai_alm.client_id'
  try {
    let id = window.localStorage.getItem(KEY)
    if (!id) {
      id = `c_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`
      window.localStorage.setItem(KEY, id)
    }
    return id
  } catch {
    return 'c_anonymous'
  }
}

function newRequestId(): string {
  return `r_${Math.random().toString(36).slice(2, 10)}`
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Id': getOrCreateClientId(),
      'X-Request-Id': newRequestId(),
      ...(init?.headers ?? {}),
    },
  })
  if (!response.ok) {
    let detail = ''
    try {
      const envelope = await response.json()
      detail =
        envelope?.error?.message ??
        envelope?.detail ??
        response.statusText ??
        `HTTP ${response.status}`
    } catch {
      detail = response.statusText || `HTTP ${response.status}`
    }
    throw new Error(detail)
  }
  return (await response.json()) as T
}

/**
 * POST /api/projects/{projectId}/runs/{runId}/finding-waivers
 *
 * Accept a single finding by writing a waiver into the run's phase4
 * dir. Idempotent on (invariant_id, finding_key).
 */
export function createFindingWaiver(
  projectId: string,
  runId: number,
  body: FindingWaiverRequest,
): Promise<FindingWaiverResponse> {
  return jsonFetch(
    `/projects/${projectId}/runs/${runId}/finding-waivers`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
}
